import { makeWarningToast, makeErrorToast, makeSuccessToast, msg } from '../../utils/makeToast'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Auth } from '@aws-amplify/auth'
import { DataStore, syncExpression } from '@aws-amplify/datastore'
import {
  Company,
  UserInfo,
  Freelancers,
  Contact,
  Invoices,
  Proposals,
  Clients,
  Projects,
  Quote,
  QuoteInfo,
  RecapInfo,
  Purchase,
  PurchaseOrder,
  Suppliers,
} from '../../models'
import i18next from 'i18next'
import LogRocket from 'logrocket'
import * as Sentry from '@sentry/react'

import { createCompanyInfo, createUserInfo } from './settings'
import { createFreelancerArchiveProject } from './projects'
import { createFreelancerInfo } from './settings'

import { API, graphqlOperation } from '@aws-amplify/api'
import * as mutations from '../../graphql/mutations'
import * as queries from '../../graphql/queries'
import { getEmailDomain } from '@src/utils/miscellaneous'

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    try {
      const user = await Auth.signIn(email, password)
      // If user is using a temporary password we signout the user and they need to update their password
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        makeWarningToast('Password Reset Required!')
        await Auth.signOut()
        return rejectWithValue('newPasswordRequired')
      }
      // Gets the relevant userInfo from the cognito user object
      const userInfo = {
        username: user.username,
        groups: user.signInUserSession.accessToken.payload['cognito:groups'],
        tenantId: user.attributes['custom:tenantid'],
        attributes: { ...user.attributes },
      }
      LogRocket.identify(userInfo.username, {
        tenantId: userInfo.tenantId,
      })
      Sentry.setUser({ email: userInfo.username })

      // ! After user is logged in we setup configuration for datastore and start the sync process.
      // ! All models we create need to be configured here to make sure only the users relevant data is synced
      // ! As of right now it's a frontend check this will require https://github.com/aws-amplify/amplify-cli/issues/317 to be solved for it to work for it to work with backend
      // If user is a company admin all models are synced for later use
      if (userInfo.groups.includes('companyAdmin')) {
        DataStore.configure({
          maxRecordsToSync: 30000000,
          syncExpressions: [
            syncExpression(Company, () => {
              return c => c.tenantId('eq', userInfo.tenantId)
            }),
            syncExpression(UserInfo, () => {
              return c => c.tenantId('eq', userInfo.tenantId)
            }),
            syncExpression(Freelancers, () => {
              return c => c.freelancerTenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Contact, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Invoices, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Proposals, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Clients, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Projects, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Purchase, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(PurchaseOrder, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Suppliers, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Quote, () => {
              return c =>
                c.tenantId('eq', userInfo.tenantId).offlineIds('contains', userInfo.username)
            }),
            syncExpression(QuoteInfo, () => {
              return c =>
                c.tenantId('eq', userInfo.tenantId).offlineIds('contains', userInfo.username)
            }),
            syncExpression(RecapInfo, () => {
              return c =>
                c.tenantId('eq', userInfo.tenantId).offlineIds('contains', userInfo.username)
            }),
          ],
        })
      }
      // If user is a freelancer on the Projects model is synced.
      if (userInfo.groups.includes('freelancer')) {
        // * Wait for this issue to be resolved https://github.com/aws-amplify/amplify-js/issues/7544
        DataStore.configure({
          maxRecordsToSync: 30000000,
          syncExpressions: [
            syncExpression(Company, () => {
              return c => c.tenantId('eq', userInfo.tenantId)
            }),
            syncExpression(UserInfo, () => {
              return c => c.tenantId('eq', userInfo.tenantId)
            }),
            syncExpression(Freelancers, () => {
              return c => c.freelancerTenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Contact, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Invoices, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Proposals, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Clients, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Projects, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Purchase, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(PurchaseOrder, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Suppliers, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Quote, () => {
              return c =>
                c
                  .childFreelancers('contains', userInfo.tenantId)
                  .offlineIds('contains', userInfo.username)
            }),
            syncExpression(QuoteInfo, () => {
              return c =>
                c
                  .childFreelancers('contains', userInfo.tenantId)
                  .offlineIds('contains', userInfo.username)
            }),
            syncExpression(RecapInfo, () => {
              return c =>
                c
                  .childFreelancers('contains', userInfo.tenantId)
                  .offlineIds('contains', userInfo.username)
            }),
          ],
        })
      }

      try {
        await DataStore.start()
        dispatch(setIsOffline({ status: true }))
      } catch (err) {
        makeErrorToast(msg)
      }
      const {
        data: { getCompany: companyInfoDetails },
      } = await API.graphql(graphqlOperation(queries.getCompany, { id: userInfo.tenantId }))

      if (!companyInfoDetails.companyEmailId) {
        const emailDomain = getEmailDomain(userInfo.username)
        console.log('userInfo.username: ', userInfo.username)
        console.log('emailDomain: ', emailDomain)
        await API.graphql(
          graphqlOperation(mutations.createUniqueEmailId, {
            companyId: userInfo.tenantId,
            emailDomain,
          })
        )
      }

      return userInfo
    } catch (err) {
      if (err.code === 'UserNotFoundException') {
        makeErrorToast(i18next.t('errors:userDoesNotExist'))
      } else {
        makeErrorToast(err.message)
      }
      return rejectWithValue(err)
    }
  }
)

export const signUpUser = createAsyncThunk(
  'auth/signUpUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          //! Currently hardcoded, it has to be made automatic
          'custom:tenantid': 'Users Company Tenant id',
        },
      })
      return email
    } catch (err) {
      makeErrorToast(err.message)
      return rejectWithValue(err)
    }
  }
)

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async ({ email, currentPassword, newPassword }, { dispatch, rejectWithValue }) => {
    try {
      const user = await Auth.signIn(email, currentPassword)
      // Temp password is updated with newPassword
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        await Auth.completeNewPassword(user, newPassword)
      }
      const userInfo = {
        username: user.username,
        groups: user.signInUserSession.accessToken.payload['cognito:groups'],
        tenantId: user.challengeParam.userAttributes['custom:tenantid'],
        attributes: { sub: user.signInUserSession.accessToken.payload.sub },
      }
      if (userInfo.groups.includes('freelancer')) {
        dispatch(createFreelancerInfo({ userInfo, email }))
        dispatch(createFreelancerArchiveProject(userInfo))
      }
      dispatch(createUserInfo(userInfo))
      if (userInfo.groups.includes('companyAdmin')) {
        dispatch(createCompanyInfo(userInfo))
      }
      return userInfo
    } catch (err) {
      makeErrorToast(err.message)
      return rejectWithValue(err)
    }
  }
)

export const confirmCode = createAsyncThunk(
  'auth/cofirmUser',
  async ({ confirmationCode }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState()
      await Auth.confirmSignUp(auth.newUserInfo.email, confirmationCode)
      makeSuccessToast('Succesfully Signed Up. Please Login')
    } catch (err) {
      if ((err.code = 'UserNotConfirmedException')) {
        makeErrorToast(err.message)
        return rejectWithValue('notConfirmed')
      }
      makeErrorToast(err.message)
      return rejectWithValue(err)
    }
  }
)

export const checkAuthenticationStatus = createAsyncThunk(
  'auth/checkAuthenticationStatus',
  async (arg, { rejectWithValue, dispatch }) => {
    try {
      const user = await Auth.currentAuthenticatedUser()
      const userInfo = {
        username: user.username,
        groups: user.signInUserSession.accessToken.payload['cognito:groups'],
        tenantId: user.attributes['custom:tenantid'],
        attributes: { ...user.attributes },
      }
      LogRocket.identify(userInfo.username, {
        tenantId: userInfo.tenantId,
      })
      Sentry.setUser({ email: userInfo.username })

      // Configures datastore according to tenantId field in schema for companyAdmins and freelancers field in schema for freelancer grp users
      if (userInfo.groups.includes('companyAdmin')) {
        DataStore.configure({
          maxRecordsToSync: 30000000,
          syncExpressions: [
            syncExpression(Company, () => {
              return c => c.tenantId('eq', userInfo.tenantId)
            }),
            syncExpression(UserInfo, () => {
              return c => c.tenantId('eq', userInfo.tenantId)
            }),
            syncExpression(Freelancers, () => {
              return c => c.freelancerTenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Contact, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Invoices, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Proposals, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Clients, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Projects, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Purchase, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(PurchaseOrder, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Suppliers, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Quote, () => {
              return c =>
                c.tenantId('eq', userInfo.tenantId).offlineIds('contains', userInfo.username)
            }),
            syncExpression(QuoteInfo, () => {
              return c =>
                c.tenantId('eq', userInfo.tenantId).offlineIds('contains', userInfo.username)
            }),
            syncExpression(RecapInfo, () => {
              return c =>
                c.tenantId('eq', userInfo.tenantId).offlineIds('contains', userInfo.username)
            }),
          ],
        })
      }

      if (userInfo.groups.includes('freelancer')) {
        DataStore.configure({
          maxRecordsToSync: 30000000,
          syncExpressions: [
            syncExpression(Company, () => {
              return c => c.tenantId('eq', userInfo.tenantId)
            }),
            syncExpression(UserInfo, () => {
              return c => c.tenantId('eq', userInfo.tenantId)
            }),
            syncExpression(Freelancers, () => {
              return c => c.freelancerTenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Contact, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Invoices, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Proposals, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Clients, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Projects, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Purchase, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(PurchaseOrder, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Suppliers, () => {
              return c => c.tenantId('eq', 'userInfo.tenantId')
            }),
            syncExpression(Quote, () => {
              return c =>
                c
                  .childFreelancers('contains', userInfo.tenantId)
                  .offlineIds('contains', userInfo.username)
            }),
            syncExpression(QuoteInfo, () => {
              return c =>
                c
                  .childFreelancers('contains', userInfo.tenantId)
                  .offlineIds('contains', userInfo.username)
            }),
            syncExpression(RecapInfo, () => {
              return c =>
                c
                  .childFreelancers('contains', userInfo.tenantId)
                  .offlineIds('contains', userInfo.username)
            }),
          ],
        })
      }

      await DataStore.start()
      dispatch(setIsOffline({ status: true }))
      return userInfo
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const logOutUser = createAsyncThunk('auth/logOut', async (arg, { rejectWithValue }) => {
  try {
    await DataStore.clear()
  } catch (err) {
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
  try {
    await DataStore.stop()
  } catch (err) {
    makeErrorToast(msg)
    return rejectWithValue(err)
  }
  try {
    await Auth.signOut()
  } catch (err) {
    makeErrorToast(err.message)
    return rejectWithValue(err)
  }
})

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (username, { rejectWithValue }) => {
    try {
      await Auth.forgotPassword(username)
    } catch (err) {
      if (err.code === 'UserNotFoundException') {
        makeErrorToast(i18next.t('errors:usernameDoesNotExist'))
        return rejectWithValue('usernameDoesNotExist')
      }
      makeErrorToast(err.message)
      return rejectWithValue(err)
    }
  }
)
export const confirmForgotPassword = createAsyncThunk(
  'auth/confirmForgotPassword',
  async ({ confirmationCode, newPassword }, { rejectWithValue, getState }) => {
    const { auth } = getState()
    try {
      await Auth.forgotPasswordSubmit(auth.newUserInfo.email, confirmationCode, newPassword)
      makeSuccessToast('Password Succesfully Updated')
    } catch (err) {
      makeErrorToast(err.message)
      return rejectWithValue(err)
    }
  }
)
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState()
      const user = await Auth.currentAuthenticatedUser()
      await Auth.changePassword(user, currentPassword, newPassword)
      makeSuccessToast('Password Successfully Updated')
    } catch (err) {
      makeErrorToast(err.message)
      return rejectWithValue(err)
    }
  }
)

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    userLoginStatus: false,
    newPasswordRequired: false,
    userInfo: {},
    newUserInfo: {},
    initialLoginApiStatus: 'idle',
    loginApiStatus: 'idle',
    signUpApiStatus: 'idle',
    updatePasswordApiStatus: 'idle',
    confirmApiStatus: 'idle',
    forgotPasswordApiStatus: 'idle',
    isOffline: false,
    error: null,
  },
  reducers: {
    setIsOffline: (state, action) => {
      state.isOffline = action.payload.status
    },
  },
  extraReducers: {
    [loginUser.pending]: (state, action) => {
      state.loginApiStatus = 'loading'
    },
    [loginUser.fulfilled]: (state, action) => {
      state.loginApiStatus = 'succeeded'
      state.confirmApiStatus = 'idle'
      state.userLoginStatus = true
      state.userInfo = { ...action.payload }
    },
    [loginUser.rejected]: (state, action) => {
      state.loginApiStatus = 'failed'
      state.userLoginStatus = false
      state.userInfo = {}
      if (action.payload === 'newPasswordRequired') {
        state.newPasswordRequired = true
      }
      state.error = action.error.message
    },
    [signUpUser.pending]: (state, action) => {
      //       state.loginApiStatus = 'loading'
      state.signUpApiStatus = 'loading'
    },
    [signUpUser.fulfilled]: (state, action) => {
      state.signUpApiStatus = 'succeeded'
      state.newUserInfo = { email: action.meta.arg.email }
    },
    [signUpUser.rejected]: (state, action) => {
      state.signUpApiStatus = 'failed'
      state.error = action.error.message
    },
    [updatePassword.pending]: (state, action) => {
      state.updatePasswordApiStatus = 'loading'
    },
    [updatePassword.fulfilled]: (state, action) => {
      state.updatePasswordApiStatus = 'succeeded'
      state.userLoginStatus = true
      state.userInfo = { ...action.payload }
    },
    [updatePassword.rejected]: (state, action) => {
      state.updatePasswordApiStatus = 'failed'
      state.userLoginStatus = false
      state.userInfo = {}
      state.error = action.error.message
    },
    [forgotPassword.pending]: (state, action) => {
      state.forgotPasswordApiStatus = 'loading'
    },
    [forgotPassword.fulfilled]: (state, action) => {
      state.forgotPasswordApiStatus = 'succeeded'
      state.newUserInfo = { email: action.meta.arg }
    },
    [forgotPassword.rejected]: (state, action) => {
      state.forgotPasswordApiStatus = 'failed'
      state.newUserInfo = {}
      state.error = action.error.message
    },
    [confirmCode.fulfilled]: (state, action) => {
      state.confirmApiStatus = 'succeeded'
      state.newUserInfo = {}
    },
    [confirmCode.rejected]: (state, action) => {
      state.confirmApiStatus = 'failed'
      state.error = action.error.message
    },
    [confirmForgotPassword.fulfilled]: (state, action) => {
      state.confirmApiStatus = 'succeeded'
      state.forgotPasswordApiStatus = 'idle'
      state.newUserInfo = {}
    },
    [confirmForgotPassword.rejected]: (state, action) => {
      state.confirmApiStatus = 'failed'
      state.newUserInfo = {}
      state.error = action.error.message
    },
    [checkAuthenticationStatus.fulfilled]: (state, action) => {
      state.initialLoginApiStatus = 'succeeded'
      state.userLoginStatus = true
      state.userInfo = { ...action.payload }
    },
    [checkAuthenticationStatus.rejected]: (state, action) => {
      state.initialLoginApiStatus = 'failed'
      state.userLoginStatus = false
      state.error = action.error.message
    },

    [logOutUser.fulfilled]: (state, action) => {
      state.userInfo = {}
      state.userLoginStatus = false
    },
    [logOutUser.rejected]: (state, action) => {
      state.error = action.error.message
    },
  },
})

export const { setIsOffline } = authSlice.actions

export default authSlice.reducer
